import { handleSearchListeners } from "../form-input-search/form-input-search.js";
const getListBoxValue = (element) => String(element?.getAttribute("data-tds-value") || element?.getAttribute("data-tds-label"));
const getListBoxLabel = (element) => String(element?.getAttribute("data-tds-label") || element?.getAttribute("data-tds-value"));
const handleListBoxListeners = ({
  container,
  input,
  listbox,
  onActiveDescendantChange,
  onClear,
  onOptionSelect
}) => {
  if (!listbox) {
    return () => null;
  }
  const handleKeyDown = (e) => {
    const id = String((input || listbox).getAttribute("aria-activedescendant"));
    const activeDescendant = document.getElementById(id);
    let targetId = void 0;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (activeDescendant) {
        targetId = (activeDescendant.nextElementSibling || listbox.firstElementChild)?.id;
      } else {
        targetId = listbox.firstElementChild?.id;
      }
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (activeDescendant) {
        targetId = (activeDescendant.previousElementSibling || listbox.lastElementChild)?.id;
      } else {
        targetId = listbox.lastElementChild?.id;
      }
    }
    if (e.key === "Home" || e.metaKey && e.key === "ArrowLeft") {
      targetId = listbox.firstElementChild?.id;
    }
    if (e.key === "End" || e.metaKey && e.key === "ArrowRight") {
      targetId = listbox.lastElementChild?.id;
    }
    if ((e.key === "Enter" || e.key === " ") && activeDescendant) {
      e.preventDefault();
      onOptionSelect({
        e,
        label: getListBoxLabel(activeDescendant),
        value: getListBoxValue(activeDescendant)
      });
    }
    if (e.key === "Tab" && activeDescendant || !e.metaKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
      targetId = "";
    }
    if (targetId !== void 0)
      onActiveDescendantChange(targetId);
  };
  (input || listbox).addEventListener("keydown", handleKeyDown);
  const handleBlur = () => {
    onActiveDescendantChange("");
  };
  (input || listbox).addEventListener("blur", handleBlur);
  const handleClick = (e) => {
    const targetOption = e.target?.closest(
      '[role="option"]'
    );
    if (targetOption) {
      const targetValue = getListBoxValue(targetOption);
      const targetLabel = getListBoxLabel(targetOption);
      onOptionSelect({ e, label: targetLabel, value: targetValue });
    }
  };
  listbox.addEventListener("click", handleClick);
  const clearBtn = (container || input?.parentElement)?.querySelector(".tds-dropdown-clear-btn");
  const handleClearClick = () => {
    if (onClear)
      onClear();
  };
  clearBtn?.addEventListener("click", handleClearClick);
  return () => {
    (input || listbox).removeEventListener("keydown", handleKeyDown);
    (input || listbox).removeEventListener("blur", handleBlur);
    listbox.removeEventListener("click", handleClick);
    clearBtn?.removeEventListener("click", handleClearClick);
  };
};
const initListBox = (listbox, props = {}) => {
  const { container, input, onOptionSelect: externalOnOptionSelect, searchInput } = props;
  const options = Array.from(listbox.querySelectorAll(".tds-listbox-option"));
  const clearBtn = searchInput?.parentElement?.querySelector(
    ".tds-form-input-search-clear"
  );
  const onActiveDescendantChange = (id) => {
    const activeDescendant = document.getElementById(String(id));
    options.forEach((option) => {
      option.classList.remove("tds--focus");
    });
    if (id) {
      activeDescendant?.classList.add("tds--focus");
      activeDescendant?.scrollIntoView({ block: "nearest" });
      (searchInput || input || listbox)?.setAttribute("aria-activedescendant", id);
    } else {
      (searchInput || input || listbox)?.removeAttribute("aria-activedescendant");
    }
  };
  const onOptionSelect = ({ e, id, label, value }) => {
    const activeDescendant = listbox.querySelector(`[data-tds-value="${value}"]`) || document.getElementById(String(id));
    const targetOption = e?.target?.closest(
      '[role="option"]'
    );
    const target = e?.type === "click" ? targetOption : activeDescendant;
    const multiple = input?.multiple || Boolean(listbox.getAttribute("aria-multiselectable"));
    if (!target || target.getAttribute("role") !== "option") {
      return;
    }
    const disabled = target?.getAttribute("aria-disabled") === "true";
    const alreadySelected = target?.hasAttribute("aria-selected");
    if (!disabled) {
      if (!multiple) {
        clearOptions();
      }
      if (alreadySelected) {
        target?.classList.remove("tds--selected");
        target?.removeAttribute("aria-selected");
      } else {
        target?.classList.add("tds--selected");
        target?.setAttribute("aria-selected", "true");
      }
      if (externalOnOptionSelect) {
        externalOnOptionSelect({ id, label, value });
      }
    }
  };
  const clearOptions = () => {
    options.forEach((option) => {
      option.classList.remove("tds--selected");
      option.removeAttribute("aria-selected");
    });
  };
  const onValueChange = (e) => {
    const value = e.target?.value || "";
    for (const option of options) {
      if (listbox.contains(option)) {
        listbox.removeChild(option);
      }
    }
    for (const option of options) {
      if (!value || getListBoxValue(option).toLowerCase().includes(value.toLowerCase()) || getListBoxLabel(option).toLowerCase().includes(value.toLowerCase())) {
        listbox.appendChild(option);
      }
    }
    searchInput?.removeAttribute("aria-activedescendant");
  };
  const searchCleanup = searchInput ? handleSearchListeners({
    clearBtn,
    clearInput: onValueChange,
    input: searchInput,
    onValueChange
  }) : () => null;
  const cleanup = handleListBoxListeners({
    container,
    input: searchInput || input,
    listbox,
    onActiveDescendantChange,
    onClear: clearOptions,
    onOptionSelect
  });
  return () => {
    searchCleanup();
    cleanup();
  };
};
const initListBoxes = (props = {}) => {
  const { parent = document } = props;
  const components = Array.from(parent.querySelectorAll(".tds-listbox"));
  components.forEach((listbox) => {
    const searchInput = parent.querySelector(`[aria-controls="${listbox.id}"]`);
    initListBox(listbox, { ...props, searchInput });
  });
};
export {
  getListBoxLabel,
  getListBoxValue,
  handleListBoxListeners,
  initListBox,
  initListBoxes
};
